/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { MenuData } from '../models';
import PropTypes from 'prop-types';
import MegaMenuModal from './MegaMenuModal';
import { coerceExtraPath, extraDepthSearch, getFirstExtraPath } from '../menu-helpers';

interface MegaMenuProps {
    data: MenuData;
}

interface MegaMenuState {
    activePath: number[];
}

export default class MegaMenu extends React.Component<MegaMenuProps, MegaMenuState> {
    private readonly rootListRef: React.RefObject<HTMLDivElement>;

    constructor(props: MegaMenuProps) {
        super(props);
        this.state = {
            activePath: [],
        };
        this.rootListRef = React.createRef();
    }

    render(): JSX.Element {
        const { data } = this.props;
        const { activePath } = this.state;

        const isOpen = activePath.length > 0 && data[activePath[0]!]?.children?.length;

        return (
            <>
                {isOpen && <div className="mega-menu__modal-backdrop" />}
                <nav className="mega-menu" onMouseLeave={() => this.close()}>
                    <div className="mega-menu__list is-level-1" ref={this.rootListRef}>
                        {data.map(({ text, href }, index) => (
                            <a
                                key={index}
                                href={href}
                                className={`mega-menu__link ${activePath[0] === index ? 'is-active' : ''}`}
                                onMouseOver={() => this.showMenu(index)}
                                onKeyDown={(e) => {
                                    if (e.key === ' ') {
                                        e.preventDefault();
                                        this.showMenu(index);
                                    }
                                }}
                                aria-label={`Press space to activate submenu`}
                            >
                                {text}
                            </a>
                        ))}
                    </div>
                    {isOpen && (
                        <MegaMenuModal
                            data={data}
                            activePath={activePath}
                            onActivate={(path) => this.setActive(path)}
                            onClose={() => this.close(true)}
                        />
                    )}
                </nav>
            </>
        );
    }

    private setActive(path: number[]): void {
        this.setState(({ activePath }) => {
            path = [activePath[0]!, ...path];
            const extraPath = extraDepthSearch(this.props.data, path);
            if (extraPath) return { activePath: extraPath };
            const parentExtraPath = coerceExtraPath(this.props.data, path);
            if (parentExtraPath.length > 0) return { activePath: parentExtraPath };
            return { activePath: getFirstExtraPath(this.props.data, activePath[0]!) };
        });
    }

    private close(focusLink = false): void {
        if (
            focusLink &&
            this.state.activePath.length > 1 &&
            typeof this.state.activePath[0] === 'number' &&
            this.rootListRef.current
        ) {
            this.rootListRef.current
                .querySelectorAll<HTMLAnchorElement>('.mega-menu__link')
                [this.state.activePath[0]]?.focus();
        }

        this.setState({ activePath: [] });
    }

    private showMenu(index: number): void {
        this.setState({ activePath: getFirstExtraPath(this.props.data, index) });
    }
}

const PropTypesExtra = PropTypes.shape({
    heading: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    img: PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string,
    }).isRequired,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(MegaMenu as any).propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string.isRequired,
            href: PropTypes.string,
            children: PropTypes.arrayOf(
                PropTypes.shape({
                    text: PropTypes.string.isRequired,
                    href: PropTypes.string,
                    extra: PropTypesExtra,
                    children: PropTypes.arrayOf(
                        PropTypes.shape({
                            text: PropTypes.string.isRequired,
                            href: PropTypes.string.isRequired,
                            extra: PropTypesExtra,
                        })
                    ),
                })
            ).isRequired,
        })
    ).isRequired,
};
